<template>
  <div v-if="limitedCuratedReviews.length" class="curated-reviews">
    <div v-if="reviewTitle || reviewCtaText" class="curated-reviews__head">
      <h2 v-if="reviewTitle" class="curated-reviews__heading">
        {{ reviewTitle }}
      </h2>

      <a v-if="isDesktopScreen && reviewCtaText" @click="handleCTAClick" class="curated-reviews__review-cta">
        {{ reviewCtaText }}
      </a>
    </div>

    <div class="curated-reviews__list">
      <div v-for="(review, index) in limitedCuratedReviews" :key="index" class="curated-reviews__item">
        <div class="curated-reviews__top">
          <div class="curated-reviews__text" :class="{ 'curated-reviews__text--expanded': expandedReviews[index] }">
            <div class="curated-reviews__text-content" v-html="review.review"></div>
          </div>
          <a v-if="isClamped[index]" class="curated-reviews__read-more" @click="toggleReadMore(index)">
            {{ expandedReviews[index] ? 'Read less' : 'Read more' }}
          </a>
          <div class="curated-reviews__separator"></div>
        </div>

        <div class="curated-reviews__bottom">
          <div class="curated-reviews__reviewer-name" v-html="review.reviewer_name"></div>

          <div v-if="review.review_count > 0" class="curated-reviews__stars">
            <svg v-for="starIndex in 5" :key="starIndex" width="16" height="14" viewBox="0 0 16 14" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <clipPath :id="`star-clip-${index}-${starIndex}`">
                  <rect :width="getStarFill(starIndex, review.review_count) * 16" height="14" />
                </clipPath>
              </defs>

              <!-- unfilled star -->
              <path
                d="M8.26904 11.2516L12.8654 14L11.6457 8.82L15.7065 5.33474L10.359 4.87789L8.26904 0L6.1791 4.87789L0.831543 5.33474L4.88498 8.82L3.67267 14L8.26904 11.2516Z"
                fill="#E5E7EB"
              />

              <!-- filled star portion -->
              <path
                d="M8.26904 11.2516L12.8654 14L11.6457 8.82L15.7065 5.33474L10.359 4.87789L8.26904 0L6.1791 4.87789L0.831543 5.33474L4.88498 8.82L3.67267 14L8.26904 11.2516Z"
                fill="#FCD34D"
                :clip-path="`url(#star-clip-${index}-${starIndex})`"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <a v-if="!isDesktopScreen && reviewCtaText" @click="handleCTAClick" class="curated-reviews__review-cta">
      {{ reviewCtaText }}
    </a>
  </div>
</template>

<script setup>
  import { toRefs, computed, ref, onMounted, nextTick } from 'vue';

  const props = defineProps({
    product: {
      type: Object,
      default: () => ({}),
    },
    isDesktopScreen: {
      type: Boolean,
      default: false,
    },
  });

  const { product, isDesktopScreen } = toRefs(props);
  
  const expandedReviews = ref([]);
  const isClamped = ref([]);

  const limitedCuratedReviews = computed(() => product.value.curated_reviews?.slice(0, 2) || []);

  // Computed properties for dynamic title and CTA text
  const reviewTitle = computed(() => {
    return product.value.curated_reviews_action?.title || "Top Reviews";
  });

  const reviewCtaText = computed(() => {
    return product.value.curated_reviews_action?.cta_name || "See more reviews";
  });

  const reviewLink = computed(() => {
    return product.value.curated_reviews_action?.link;
  });

  onMounted(() => {
    nextTick(() => {
      const reviewEls = document.querySelectorAll('.curated-reviews__text');
      isClamped.value = Array.from(reviewEls).map((el) => el.scrollHeight > el.clientHeight);
      expandedReviews.value = Array(reviewEls.length).fill(false);
    });
  });

  // Returns a fill percentage (0 to 1) for a given star index
  const getStarFill = (index, rating) => {
    const diff = rating - (index - 1);
    return Math.max(0, Math.min(1, diff));
  };

  const handleCTAClick = () => {
    if (reviewLink.value) {
      // If a link is provided, redirect to that link
      window.open(reviewLink.value, '_blank');
    } else {
      // Otherwise, navigate to reviews section as before
      navigateToReviews();
    }
  };

  const navigateToReviews = () => {
    const el = document.getElementById('reviews');

    if (el) {
      const yOffset = isDesktopScreen.value ? -107 : -74;
      const yPosition = el.getBoundingClientRect().top + window.scrollY + yOffset;

      window.scrollTo({
        top: yPosition,
        behavior: 'smooth',
      });
    }
  };

  const toggleReadMore = (index) => {
    expandedReviews.value[index] = !expandedReviews.value[index];
  };
</script>
